/**
 * Firebase Authentication Hook
 * Handles Google, Email/Password, and Anonymous (Guest) authentication
 * with guest data migration and account linking
 */
import { useState, useCallback, useEffect } from 'react';
import { onAuthStateChanged, GoogleAuthProvider } from 'firebase/auth';
import { 
    auth, 
    googleProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInAnonymously,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    linkWithPopup,
    signOut
} from '@/lib/firebase';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = 'astral_belonging_state';
const SESSION_TOKEN_KEY = 'session_token';

export const useFirebaseAuth = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [firebaseUser, setFirebaseUser] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [isMigrationComplete, setIsMigrationComplete] = useState(false);  // Track migration status

    /**
     * Automatic Token Refresh Handler
     * Firebase ID tokens expire after 1 hour. This effect sets up a timer
     * to proactively refresh the token at 50 minutes to prevent auth errors.
     */
    useEffect(() => {
        if (!firebaseUser) return;
        
        // Token refresh interval: 50 minutes (tokens expire at 60 min)
        const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000; // 50 minutes in ms
        
        const refreshToken = async () => {
            try {
                // Force refresh the token
                const newToken = await firebaseUser.getIdToken(true);
                console.log('🔄 Firebase ID token refreshed silently');
                
                // Dispatch event for any components that cache the token
                window.dispatchEvent(new CustomEvent('firebase-token-refreshed', { 
                    detail: { timestamp: Date.now() } 
                }));
                
                return newToken;
            } catch (err) {
                console.error('❌ Token refresh failed:', err);
                // If refresh fails due to session invalidation, sign out
                if (err.code === 'auth/user-token-expired' || err.code === 'auth/user-disabled') {
                    console.log('🚪 Session invalidated, signing out...');
                    await signOut(auth);
                }
            }
        };
        
        // Initial token refresh check - refresh if token is old
        const checkInitialToken = async () => {
            try {
                const tokenResult = await firebaseUser.getIdTokenResult();
                const issuedAt = new Date(tokenResult.issuedAtTime).getTime();
                const tokenAge = Date.now() - issuedAt;
                
                // If token is older than 45 minutes, refresh immediately
                if (tokenAge > 45 * 60 * 1000) {
                    console.log('🔄 Token is old, refreshing immediately...');
                    await refreshToken();
                }
            } catch (err) {
                console.error('Token age check failed:', err);
            }
        };
        
        checkInitialToken();
        
        // Set up interval for periodic refresh
        const intervalId = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
        console.log('⏰ Token refresh timer started (every 50 min)');
        
        // Cleanup on unmount or user change
        return () => {
            clearInterval(intervalId);
            console.log('⏰ Token refresh timer cleared');
        };
    }, [firebaseUser]);

    /**
     * Listen for Firebase auth state changes on mount
     * Also handles redirect result from Google sign-in
     */
    useEffect(() => {
        // Check for redirect result first
        const handleRedirectResult = async () => {
            // Check if we're expecting a redirect result
            // Using localStorage because sessionStorage is cleared during redirects
            const authPending = localStorage.getItem('google_auth_pending');
            console.log('🔍 Checking redirect result... authPending:', authPending);
            
            try {
                const result = await getRedirectResult(auth);
                console.log('🔍 getRedirectResult returned:', result ? 'User found' : 'null');
                
                if (result) {
                    console.log('🔵 Google sign-in successful (redirect):', result.user.uid);
                    localStorage.removeItem('google_auth_pending');
                    
                    // Get stored guest data if any
                    const storedGuestData = localStorage.getItem('pending_guest_data');
                    console.log('🔍 Stored guest data:', storedGuestData ? 'found' : 'none');
                    const guestData = storedGuestData ? JSON.parse(storedGuestData) : null;
                    localStorage.removeItem('pending_guest_data');
                    
                    // Authenticate with backend
                    const idToken = await result.user.getIdToken();
                    const response = await fetch(`${API_URL}/api/auth/firebase`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ 
                            id_token: idToken,
                            guest_data: guestData,
                            is_anonymous: false
                        })
                    });
                    
                    if (response.ok) {
                        const userData = await response.json();
                        console.log('✅ Redirect auth successful:', userData.user_id);
                        
                        if (userData.session_token) {
                            localStorage.setItem(SESSION_TOKEN_KEY, userData.session_token);
                        }
                        
                        // CRITICAL: Update localStorage to show marketplace after auth
                        // This ensures the dashboard renders on next check
                        const onboardingData = localStorage.getItem('astral_belonging_onboarding');
                        if (onboardingData) {
                            try {
                                const parsed = JSON.parse(onboardingData);
                                parsed.showMarketplace = true;
                                parsed.currentStep = 7;
                                localStorage.setItem('astral_belonging_onboarding', JSON.stringify(parsed));
                                console.log('📋 Updated localStorage with showMarketplace=true');
                            } catch (e) {
                                console.warn('Failed to update onboarding data:', e);
                            }
                        } else if (guestData) {
                            // Create the localStorage entry if it doesn't exist but we have guest data
                            const newOnboardingData = {
                                userData: guestData,
                                currentStep: 7,
                                completedSteps: [0, 1, 2, 3, 4, 5, 6],
                                showMarketplace: true
                            };
                            localStorage.setItem('astral_belonging_onboarding', JSON.stringify(newOnboardingData));
                            console.log('📋 Created localStorage with showMarketplace=true');
                        }
                        
                        setUser(userData);
                        setIsMigrationComplete(true);  // Mark migration as complete
                        setIsLoading(false);
                        
                        // Force page reload to pick up new auth state
                        window.location.reload();
                    }
                } else if (authPending) {
                    // We were expecting a result but didn't get one from getRedirectResult
                    // This can happen - wait for onAuthStateChanged to fire instead
                    console.log('⏳ Redirect auth pending but getRedirectResult returned null - waiting for onAuthStateChanged...');
                    // DON'T remove google_auth_pending yet - let onAuthStateChanged handle it
                    // DON'T set loading to false yet
                }
            } catch (err) {
                console.error('Redirect result error:', err.code || err.message);
                localStorage.removeItem('google_auth_pending');
                localStorage.removeItem('pending_guest_data');
                setError('Google sign-in failed. Please try again.');
                setIsLoading(false);
            }
        };
        
        handleRedirectResult();
        
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
            const authPending = localStorage.getItem('google_auth_pending');
            console.log('🔐 Auth state changed:', fbUser?.uid || 'No user', fbUser?.isAnonymous ? '(anonymous)' : '', 'authPending:', !!authPending);
            setFirebaseUser(fbUser);
            setIsAnonymous(fbUser?.isAnonymous || false);
            
            // If auth is pending but no user, the redirect didn't work
            // This happens on preview domains due to cross-origin issues
            if (authPending && !fbUser) {
                console.log('⚠️ Auth pending but no user - redirect auth failed (cross-origin issue)');
                // Clear pending state and let user try again
                localStorage.removeItem('google_auth_pending');
                localStorage.removeItem('pending_guest_data');
                setError('Google sign-in failed due to domain restrictions. Please try again.');
                setIsLoading(false);
                setIsInitialized(true);
                return;
            }
            
            // Handle ANY Firebase user (including anonymous/guests)
            if (fbUser) {
                // We have a user (could be anonymous/guest or real user)
                console.log('🔵 Firebase user detected:', fbUser.uid, fbUser.isAnonymous ? '(guest)' : fbUser.email);
                
                // If we were waiting for redirect auth (non-anonymous), process it now
                if (authPending && !fbUser.isAnonymous) {
                    console.log('✅ Processing pending redirect auth via onAuthStateChanged');
                    localStorage.removeItem('google_auth_pending');
                    
                    // Get stored guest data if any
                    const storedGuestData = localStorage.getItem('pending_guest_data');
                    const guestData = storedGuestData ? JSON.parse(storedGuestData) : null;
                    localStorage.removeItem('pending_guest_data');
                    
                    try {
                        // Authenticate with backend
                        const idToken = await fbUser.getIdToken();
                        const response = await fetch(`${API_URL}/api/auth/firebase`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ 
                                id_token: idToken,
                                guest_data: guestData,
                                is_anonymous: false
                            })
                        });
                        
                        if (response.ok) {
                            const userData = await response.json();
                            console.log('✅ Backend auth successful (via onAuthStateChanged):', userData.user_id);
                            
                            if (userData.session_token) {
                                localStorage.setItem(SESSION_TOKEN_KEY, userData.session_token);
                            }
                            
                            // Update localStorage to show marketplace
                            const onboardingData = localStorage.getItem('astral_belonging_onboarding');
                            if (onboardingData) {
                                try {
                                    const parsed = JSON.parse(onboardingData);
                                    parsed.showMarketplace = true;
                                    parsed.currentStep = 7;
                                    localStorage.setItem('astral_belonging_onboarding', JSON.stringify(parsed));
                                } catch (e) {}
                            } else if (guestData) {
                                const newOnboardingData = {
                                    userData: guestData,
                                    currentStep: 7,
                                    completedSteps: [0, 1, 2, 3, 4, 5, 6],
                                    showMarketplace: true
                                };
                                localStorage.setItem('astral_belonging_onboarding', JSON.stringify(newOnboardingData));
                            }
                            
                            setUser(userData);
                            setIsMigrationComplete(true);
                            setIsLoading(false);
                            setIsInitialized(true);
                            
                            // Reload to pick up new auth state
                            window.location.reload();
                            return;
                        }
                    } catch (err) {
                        console.error('Failed to authenticate with backend:', err);
                    }
                } else {
                    // Normal auth state restoration (not from redirect)
                    // This handles both regular users AND anonymous/guest users on page refresh
                    try {
                        const idToken = await fbUser.getIdToken();
                        const response = await fetch(`${API_URL}/api/auth/firebase`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ 
                                id_token: idToken,
                                is_anonymous: fbUser.isAnonymous
                            })
                        });
                        
                        if (response.ok) {
                            const userData = await response.json();
                            console.log('✅ Auth state restored:', userData.user_id, fbUser.isAnonymous ? '(guest)' : '');
                            
                            // Store session token
                            if (userData.session_token) {
                                localStorage.setItem(SESSION_TOKEN_KEY, userData.session_token);
                            }
                            
                            setUser(userData);
                        } else {
                            console.warn('❌ Backend auth restoration failed:', response.status);
                            // For guests, we should still show them their onboarding data
                            if (fbUser.isAnonymous) {
                                const onboardingData = localStorage.getItem('astral_belonging_onboarding');
                                if (onboardingData) {
                                    console.log('📦 Restoring guest from onboarding data');
                                    setUser({ user_id: fbUser.uid, is_anonymous: true });
                                }
                            }
                        }
                    } catch (err) {
                        console.error('Failed to restore auth state:', err);
                        // For guests, fallback to localStorage
                        if (fbUser.isAnonymous) {
                            const onboardingData = localStorage.getItem('astral_belonging_onboarding');
                            if (onboardingData) {
                                console.log('📦 Restoring guest from onboarding data (after error)');
                                setUser({ user_id: fbUser.uid, is_anonymous: true });
                            }
                        }
                    }
                }
            } else {
                // No Firebase user - but check for existing guest session token
                const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
                if (storedToken) {
                    console.log('🔍 No Firebase user, but found session token - attempting guest session restoration...');
                    try {
                        // Validate session with backend
                        const response = await fetch(`${API_URL}/api/auth/validate-session`, {
                            method: 'POST',
                            headers: { 
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${storedToken}`
                            },
                            credentials: 'include'
                        });
                        
                        if (response.ok) {
                            const userData = await response.json();
                            if (userData.is_valid && userData.user) {
                                console.log('✅ Guest session restored:', userData.user.user_id);
                                setUser(userData.user);
                                setIsAnonymous(userData.user.is_anonymous || false);
                            } else {
                                console.log('❌ Session token invalid, clearing...');
                                localStorage.removeItem(SESSION_TOKEN_KEY);
                                setUser(null);
                            }
                        } else {
                            console.log('❌ Session validation failed, clearing token...');
                            localStorage.removeItem(SESSION_TOKEN_KEY);
                            setUser(null);
                        }
                    } catch (err) {
                        console.error('Failed to validate session:', err);
                        // Don't clear token on network error - might be temporary
                        setUser(null);
                    }
                } else {
                    setUser(null);
                }
            }
            
            setIsLoading(false);
            setIsInitialized(true);
        });

        return () => unsubscribe();
    }, []);

    /**
     * Authenticate with backend using Firebase ID token
     * CRITICAL: Stores session_token to localStorage for persistence
     */
    const authenticateWithBackend = useCallback(async (firebaseUser, guestData = null) => {
        try {
            const idToken = await firebaseUser.getIdToken();
            
            const response = await fetch(`${API_URL}/api/auth/firebase`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    id_token: idToken,
                    guest_data: guestData,
                    is_anonymous: firebaseUser.isAnonymous
                })
            });

            // Clone response before reading to avoid "body stream already read" error
            const responseClone = response.clone();
            
            if (!response.ok) {
                let errorMessage = 'Authentication failed';
                try {
                    const errorData = await responseClone.json();
                    errorMessage = errorData.detail || errorMessage;
                    console.error('🔴 Backend auth error:', errorMessage);
                } catch (e) {
                    // Response body might be empty or not JSON
                    console.warn('Could not parse error response:', e);
                }
                throw new Error(errorMessage);
            }

            const userData = await response.json();
            console.log('✅ Backend auth successful:', userData);
            
            // Store session token
            if (userData.session_token) {
                localStorage.setItem(SESSION_TOKEN_KEY, userData.session_token);
                console.log('🔑 Session token stored');
            }
            
            // Also store avatar URL in astral_user for quick access
            if (userData.avatar_url || userData.picture) {
                const userToStore = { ...userData };
                localStorage.setItem('astral_user', JSON.stringify(userToStore));
            }
            
            // CRITICAL: Update localStorage to show marketplace after auth
            // This ensures the dashboard renders after email login
            const onboardingData = localStorage.getItem('astral_belonging_onboarding');
            if (onboardingData) {
                try {
                    const parsed = JSON.parse(onboardingData);
                    parsed.showMarketplace = true;
                    parsed.currentStep = 7;
                    localStorage.setItem('astral_belonging_onboarding', JSON.stringify(parsed));
                    console.log('📋 Updated localStorage with showMarketplace=true');
                } catch (e) {
                    console.warn('Failed to update onboarding data:', e);
                }
            } else if (guestData) {
                // Create the localStorage entry if it doesn't exist but we have guest data
                const newOnboardingData = {
                    userData: guestData,
                    currentStep: 7,
                    completedSteps: [0, 1, 2, 3, 4, 5, 6],
                    showMarketplace: true
                };
                localStorage.setItem('astral_belonging_onboarding', JSON.stringify(newOnboardingData));
                console.log('📋 Created localStorage with showMarketplace=true');
            }
            
            setUser(userData);
            setIsAnonymous(firebaseUser.isAnonymous);
            setIsMigrationComplete(true);  // Mark migration as complete
            return userData;
            
        } catch (err) {
            console.error('Backend auth error:', err);
            throw err;
        }
    }, []);

    /**
     * Sign in with Google
     * Using signInWithPopup because redirect doesn't work on preview domains
     * due to cross-origin auth state not being transferred
     */
    const signInWithGoogle = useCallback(async (guestData = null) => {
        setIsLoading(true);
        setError(null);

        try {
            console.log('🔵 Starting Google sign-in with POPUP...');
            
            // SHADOW PROFILE HANDSHAKE: Auto-extract guest data from localStorage if not provided
            let dataToMigrate = guestData;
            if (!dataToMigrate) {
                const onboardingData = localStorage.getItem('astral_belonging_onboarding');
                if (onboardingData) {
                    try {
                        const parsed = JSON.parse(onboardingData);
                        if (parsed.userData) {
                            dataToMigrate = {
                                name: parsed.userData.name,
                                birthDate: parsed.userData.birthDate,
                                birthTime: parsed.userData.birthTime,
                                location: parsed.userData.location,
                                locationLat: parsed.userData.locationLat,
                                locationLng: parsed.userData.locationLng,
                                utcOffsetMinutes: parsed.userData.utcOffsetMinutes,
                                timeOfDay: parsed.userData.timeOfDay,
                                unknownTime: parsed.userData.unknownTime,
                                mood: parsed.userData.mood,
                                focus: parsed.userData.focus,
                                genderIdentity: parsed.userData.genderIdentity,
                                avatarUrl: parsed.userData.avatarUrl
                            };
                            console.log('📋 Auto-extracted guest data for migration:', dataToMigrate.name);
                        }
                    } catch (e) {
                        console.warn('Failed to parse onboarding data for migration:', e);
                    }
                }
            }
            
            // Use POPUP - works better on preview domains
            const result = await signInWithPopup(auth, googleProvider);
            console.log('🔵 Google sign-in successful (popup):', result.user.uid);
            
            const userData = await authenticateWithBackend(result.user, dataToMigrate);
            setIsLoading(false);
            return userData;

        } catch (err) {
            console.error('Google sign-in error:', err.code, err.message);
            
            let errorMessage = 'Google sign-in failed. Please try again.';
            
            if (err.code === 'auth/popup-closed-by-user') {
                errorMessage = 'Sign-in cancelled.';
            } else if (err.code === 'auth/popup-blocked') {
                errorMessage = 'Popup was blocked. Please allow popups for this site.';
            } else if (err.code === 'auth/network-request-failed') {
                errorMessage = 'Network error. Please check your connection and try again.';
            } else if (err.code === 'auth/unauthorized-domain') {
                errorMessage = 'This domain is not authorized for Google sign-in.';
            }
            
            setError(errorMessage);
            setIsLoading(false);
            throw new Error(errorMessage);
        }
    }, [authenticateWithBackend]);

    /**
     * Sign up with Email/Password
     * SHADOW PROFILE HANDSHAKE: Auto-extracts guest data for migration
     */
    const signUpWithEmail = useCallback(async (email, password, guestData = null) => {
        setIsLoading(true);
        setError(null);

        try {
            // SHADOW PROFILE HANDSHAKE: Auto-extract guest data if not provided
            let dataToMigrate = guestData;
            if (!dataToMigrate) {
                const onboardingData = localStorage.getItem('astral_belonging_onboarding');
                if (onboardingData) {
                    try {
                        const parsed = JSON.parse(onboardingData);
                        if (parsed.userData) {
                            dataToMigrate = {
                                name: parsed.userData.name,
                                birthDate: parsed.userData.birthDate,
                                birthTime: parsed.userData.birthTime,
                                location: parsed.userData.location,
                                locationLat: parsed.userData.locationLat,
                                locationLng: parsed.userData.locationLng,
                                utcOffsetMinutes: parsed.userData.utcOffsetMinutes,
                                timeOfDay: parsed.userData.timeOfDay,
                                unknownTime: parsed.userData.unknownTime,
                                mood: parsed.userData.mood,
                                focus: parsed.userData.focus,
                                genderIdentity: parsed.userData.genderIdentity,
                                avatarUrl: parsed.userData.avatarUrl
                            };
                            console.log('📋 Auto-extracted guest data for email signup migration');
                        }
                    } catch (e) {
                        console.warn('Failed to parse onboarding data for migration:', e);
                    }
                }
            }
            
            const result = await createUserWithEmailAndPassword(auth, email, password);
            console.log('📧 Email sign-up successful:', result.user.uid);
            
            const userData = await authenticateWithBackend(result.user, dataToMigrate);
            setIsLoading(false);
            return userData;

        } catch (err) {
            console.error('Email sign-up error:', err);
            
            let errorMessage = 'Sign-up failed. Please try again.';
            if (err.code === 'auth/email-already-in-use') {
                errorMessage = 'This email is already registered. Try signing in instead.';
            } else if (err.code === 'auth/weak-password') {
                errorMessage = 'Password must be at least 6 characters.';
            } else if (err.code === 'auth/invalid-email') {
                errorMessage = 'Please enter a valid email address.';
            } else if (err.code === 'auth/network-request-failed') {
                // Firebase sometimes returns network-request-failed when email already exists
                errorMessage = 'This email may already be registered. Try signing in, or use a different email.';
            } else if (err.code === 'auth/too-many-requests') {
                errorMessage = 'Too many attempts. Please wait a moment and try again.';
            }
            
            setError(errorMessage);
            setIsLoading(false);
            throw new Error(errorMessage);
        }
    }, [authenticateWithBackend]);

    /**
     * Sign in with Email/Password
     * SHADOW PROFILE HANDSHAKE: Auto-extracts guest data for migration
     */
    const signInWithEmail = useCallback(async (email, password) => {
        setIsLoading(true);
        setError(null);

        try {
            // SHADOW PROFILE HANDSHAKE: Auto-extract guest data for migration
            let dataToMigrate = null;
            const onboardingData = localStorage.getItem('astral_belonging_onboarding');
            if (onboardingData) {
                try {
                    const parsed = JSON.parse(onboardingData);
                    if (parsed.userData && parsed.userData.birthDate) {
                        dataToMigrate = {
                            name: parsed.userData.name,
                            birthDate: parsed.userData.birthDate,
                            birthTime: parsed.userData.birthTime,
                            location: parsed.userData.location,
                            locationLat: parsed.userData.locationLat,
                            locationLng: parsed.userData.locationLng,
                            utcOffsetMinutes: parsed.userData.utcOffsetMinutes,
                            timeOfDay: parsed.userData.timeOfDay,
                            unknownTime: parsed.userData.unknownTime,
                            mood: parsed.userData.mood,
                            focus: parsed.userData.focus,
                            genderIdentity: parsed.userData.genderIdentity,
                            avatarUrl: parsed.userData.avatarUrl
                        };
                        console.log('📋 Auto-extracted guest data for email signin migration');
                    }
                } catch (e) {
                    console.warn('Failed to parse onboarding data for migration:', e);
                }
            }
            
            const result = await signInWithEmailAndPassword(auth, email, password);
            console.log('📧 Email sign-in successful:', result.user.uid);
            
            const userData = await authenticateWithBackend(result.user, dataToMigrate);
            setIsLoading(false);
            return userData;

        } catch (err) {
            console.error('Email sign-in error:', err);
            
            let errorMessage = 'Sign-in failed. Please check your credentials.';
            if (err.code === 'auth/user-not-found') {
                errorMessage = 'No account found with this email. Would you like to create one?';
            } else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                errorMessage = 'Incorrect password. Try again or reset your password.';
            } else if (err.code === 'auth/too-many-requests') {
                errorMessage = 'Too many attempts. Please wait a moment and try again.';
            } else if (err.code === 'auth/user-disabled') {
                errorMessage = 'This account has been disabled. Contact support for help.';
            } else if (err.code === 'auth/invalid-email') {
                errorMessage = 'Please enter a valid email address.';
            }
            
            setError(errorMessage);
            setIsLoading(false);
            throw new Error(errorMessage);
        }
    }, [authenticateWithBackend]);

    /**
     * Send password reset email
     */
    const resetPassword = useCallback(async (email) => {
        setIsLoading(true);
        setError(null);

        try {
            await sendPasswordResetEmail(auth, email);
            console.log('📧 Password reset email sent to:', email);
            setIsLoading(false);
            return true;

        } catch (err) {
            console.error('Password reset error:', err);
            
            let errorMessage = 'Failed to send reset email.';
            if (err.code === 'auth/user-not-found') {
                errorMessage = 'No account found with this email.';
            } else if (err.code === 'auth/invalid-email') {
                errorMessage = 'Please enter a valid email address.';
            }
            
            setError(errorMessage);
            setIsLoading(false);
            throw new Error(errorMessage);
        }
    }, []);

    /**
     * Sign in as Guest (Local guest mode)
     * Creates a local guest session without Firebase
     * Data can be upgraded to a full account later
     */
    const signInAsGuest = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            // Create a local guest session via backend
            const response = await fetch(`${API_URL}/api/auth/guest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to create guest session');
            }

            const userData = await response.json();
            console.log('👻 Guest sign-in successful:', userData.user_id);
            
            // Store session token
            if (userData.session_token) {
                localStorage.setItem(SESSION_TOKEN_KEY, userData.session_token);
                console.log('🔑 Session token stored');
            }
            
            setUser(userData);
            setIsAnonymous(true);
            setIsLoading(false);
            return userData;

        } catch (err) {
            console.error('Guest sign-in error:', err);
            setError('Failed to start guest session. Please try again.');
            setIsLoading(false);
            throw err;
        }
    }, []);

    /**
     * Link guest account to Google
     * Upgrades a guest account to a permanent Google account
     */
    const linkGuestToGoogle = useCallback(async () => {
        // Check if user is a guest
        if (!user || !user.is_anonymous) {
            throw new Error('No guest account to link');
        }

        setIsLoading(true);
        setError(null);

        try {
            // Sign in with Google
            const result = await signInWithPopup(auth, googleProvider);
            console.log('🔗 Google sign-in for linking:', result.user.uid);
            
            // Get Firebase ID token
            const idToken = await result.user.getIdToken();
            
            // Call backend to link guest account to Google
            const response = await fetch(`${API_URL}/api/auth/link-guest`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem(SESSION_TOKEN_KEY)}`
                },
                credentials: 'include',
                body: JSON.stringify({
                    id_token: idToken,
                    guest_user_id: user.user_id
                })
            });

            if (!response.ok) {
                let errorMessage = 'Failed to link account';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorMessage;
                } catch (e) {
                    console.warn('Could not parse error response:', e);
                }
                throw new Error(errorMessage);
            }

            const userData = await response.json();
            console.log('🔗 Guest account linked successfully:', userData.user_id);
            
            // Update session token
            if (userData.session_token) {
                localStorage.setItem(SESSION_TOKEN_KEY, userData.session_token);
            }
            
            setUser(userData);
            setIsAnonymous(false);
            setIsLoading(false);
            return userData;

        } catch (err) {
            console.error('Account linking error:', err);
            
            let errorMessage = 'Failed to link account. Please try again.';
            if (err.code === 'auth/popup-closed-by-user') {
                errorMessage = 'Linking cancelled.';
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
            setIsLoading(false);
            throw new Error(errorMessage);
        }
    }, [user]);

    /**
     * Sign out - clears ALL local data
     */
    const logout = useCallback(async (onComplete) => {
        try {
            setIsLoading(true);
            
            await signOut(auth);
            
            await fetch(`${API_URL}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });

            // Clear ALL local storage keys related to the app
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem('astral_belonging_onboarding');
            localStorage.removeItem(SESSION_TOKEN_KEY);
            localStorage.removeItem('astral_user');
            localStorage.removeItem('google_auth_pending');
            localStorage.removeItem('pending_guest_data');
            
            // Clear any cached profile data
            sessionStorage.clear();
            
            // Reset all state
            setUser(null);
            setFirebaseUser(null);
            setIsAnonymous(false);
            setIsMigrationComplete(false);
            
            console.log('✅ Logged out successfully - all data cleared');
            setIsLoading(false);
            
            if (onComplete) onComplete();
            
        } catch (err) {
            console.error('Logout error:', err);
            setIsLoading(false);
            // Even on error, clear local data
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(SESSION_TOKEN_KEY);
            localStorage.removeItem('astral_belonging_onboarding');
            localStorage.removeItem('astral_user');
            sessionStorage.clear();
            setUser(null);
            setFirebaseUser(null);
            if (onComplete) onComplete();
        }
    }, []);

    return {
        // State
        user,
        firebaseUser,
        isLoading,
        isInitialized,
        error,
        isAnonymous,
        isMigrationComplete,  // Track when data migration is complete
        
        // Google auth
        signInWithGoogle,
        
        // Email auth
        signUpWithEmail,
        signInWithEmail,
        resetPassword,
        
        // Guest auth
        signInAsGuest,
        linkGuestToGoogle,
        
        // General
        logout,
        setError
    };
};

export default useFirebaseAuth;
